const CHANNEL='joliemap.shopify',PROTOCOL=1;
const formats={'30x40':[300,400],'50x70':[500,700],'70x100':[700,1000]};
const key=value=>`${value.page_mm?.join(',')}|${value.support}|${value.frame}`;
const numericId=value=>typeof value==='string' && /^[1-9]\d{0,19}$/.test(value);
const supportLabels={paper_250:'Papier mat 250 g/m²',canvas_360:'Toile coton canvas 360 g/m²'};
const styleLabels={nationale6:'Nationale 6'};
const styleLabel=value=>styleLabels[value] || String(value || '').split(/[_-]/).map(word=>word.charAt(0).toUpperCase()+word.slice(1)).join(' ');
const frameLabels={none:'Sans cadre',oak:'Cadre en chêne',oak_hanger:'Cadre baguette en chêne'};
const publicError=(message,retryable=false)=>Object.assign(new Error(message),{retryable});
export function expandMappings(settings) {
  const result=[],seen=new Set();
  for(const row of settings.mappings || []) {
    if(!formats[row.format] || !Object.hasOwn(supportLabels,row.support) || !numericId(row.variant_id))continue;
    for(const page of [formats[row.format],[...formats[row.format]].reverse()]) {
      const base={page_mm:[...page],support:row.support,frame:'none',id:row.variant_id};
      if(seen.has(key(base)))throw publicError('Le catalogue contient un format en double.');
      seen.add(key(base));result.push(base);
      const frame=row.format==='30x40'?settings.oak:row.format==='50x70' && page[0]===500?settings.hanger:null;
      if(frame?.handle && numericId(frame.variant_id))result.push({...base,frame:row.format==='30x40'?'oak':'oak_hanger',
        frame_id:frame.variant_id,frame_handle:frame.handle});
    }
  }
  return result;
}
function productVariant(product,id) {return product?.variants?.find(variant=>String(variant.id)===id);}
export function buildCatalog(settings,products,currency) {
  const mappings=expandMappings(settings);
  const variants=mappings.map(mapping=>{
    const base=productVariant(products[settings.product_handle],mapping.id);
    const frame=mapping.frame_id?productVariant(products[mapping.frame_handle],mapping.frame_id):null;
    if(!base || (mapping.frame_id && !frame) || !Number.isSafeInteger(base.price) || (frame && !Number.isSafeInteger(frame.price))) {
      throw publicError('Les variantes du catalogue ne sont pas encore configurées.');
    }
    return {...mapping,price:base.price+(frame?.price || 0),available:base.available===true && (!frame || frame.available===true)};
  });
  return {currency,enabled:true,variants};
}
export function publicCatalog(catalog) {
  return {currency:catalog.currency,enabled:catalog.enabled,variants:catalog.variants.map(({id,page_mm,support,frame,price,available})=>({id,page_mm,support,frame,price,available}))};
}
export function savedCartItems(composition,variant,saved,requestId,backendOrigin=null) {
  const props=saved?.cart_properties;
  if(typeof saved?.id!=='string' || !saved.id || props?._joliemap_id!==saved.id || !/^[a-f0-9]{64}$/.test(props?._joliemap_sha256) || props?._joliemap_schema!=='1') {
    throw publicError('La sauvegarde de votre carte n’a pas pu être vérifiée.');
  }
  let preview=null;
  if(saved.preview_url) {
    try {const url=new URL(saved.preview_url);if(url.protocol!=='https:' || url.username || url.password || (backendOrigin && url.origin!==backendOrigin) || !/^\/v1\/previews\/[0-9a-f-]{36}\/[A-Za-z0-9_-]{32,128}$/.test(url.pathname) || url.pathname.split('/')[3]!==saved.id || url.search || url.hash)throw new Error();preview=url.href;}
    catch {throw publicError('L’aperçu enregistré de votre carte n’a pas pu être vérifié.');}
  }
  const conf=composition.config;
  const items=[{id:variant.id,quantity:1,properties:{
    _joliemap_id:saved.id,_joliemap_sha256:props._joliemap_sha256,_joliemap_schema:'1',_joliemap_request:requestId,
    ...(variant.frame_id?{_joliemap_frame_variant:variant.frame_id}:{}),
    ...(preview?{_joliemap_preview:preview}:{}),
    'Lieu':conf.title,'Style':styleLabel(conf.style_id),'Sous-titre':conf.subtitle,'Texte':conf.caption,
    'Format':`${conf.page_mm[0]/10} × ${conf.page_mm[1]/10} cm`,'Support':supportLabels[conf.support],
    'Cadre':frameLabels[conf.frame],
  }}];
  if(variant.frame_id)items.push({id:variant.frame_id,quantity:1,properties:{_joliemap_frame_for:saved.id,_joliemap_request:requestId,...(preview?{_joliemap_preview:preview}:{}),'Pour la carte':`${conf.title} · ${styleLabel(conf.style_id)}`}});
  return items;
}
export function missingCartItems(items,cart,requestId) {
  if(!Array.isArray(cart?.items))throw publicError('Le panier ne peut pas être vérifié. Réessayez dans un instant.',true);
  return items.filter(item=>!cart.items.some(line=>String(line.variant_id || line.id)===item.id && line.quantity>=1 &&
    line.properties?._joliemap_request===requestId &&
    (item.properties._joliemap_id ? line.properties._joliemap_id===item.properties._joliemap_id :
      line.properties._joliemap_frame_for===item.properties._joliemap_frame_for)));
}
export function createShopifyParent({settings,fetchImpl=fetch,origin,root='/',onCatalog=()=>{},onAdded=()=>{}}) {
  const base=new URL(root,origin);
  if(base.origin!==origin || !base.pathname.endsWith('/'))throw publicError('Le chemin de la boutique est invalide.');
  const enabled=settings.enable_cart===true && Boolean(settings.backend_url && settings.product_handle && settings.mappings?.length);
  let endpoint=null,catalog={currency:settings.currency || 'EUR',enabled:false,variants:[]};
  if(enabled) {
    endpoint=new URL(settings.backend_url);
    if(endpoint.protocol!=='https:' || endpoint.username || endpoint.password || endpoint.search || endpoint.hash)throw publicError('L’adresse de sauvegarde est invalide.');
    endpoint.pathname=endpoint.pathname.replace(/\/$/,'')+'/v1/compositions';
  }
  const requests=new Map(),states=new Map();
  const fingerprints=new Map();
  async function json(url,options={}) {
    let response;
    try {response=await fetchImpl(url,{cache:'no-store',...options,signal:AbortSignal.timeout(25000)});}
    catch {throw publicError('La connexion a été interrompue. Réessayez pour vérifier le panier.',true);}
    let data;try{data=await response.json();}catch{throw publicError('La réponse de la boutique est indisponible. Réessayez.',true);}
    if(!response.ok)throw publicError(response.status===422?'Un article n’est plus disponible dans cette quantité. Vérifiez votre panier.':
      'La boutique n’a pas pu terminer cet ajout. Réessayez dans un instant.',response.status>=500);
    return data;
  }
  async function refresh() {
    if(!enabled){onCatalog(publicCatalog(catalog));return catalog;}
    const handles=[...new Set([settings.product_handle,...expandMappings(settings).map(row=>row.frame_handle).filter(Boolean)])];
    const values=await Promise.all(handles.map(handle=>{
      if(!/^[a-z0-9][a-z0-9-]{0,255}$/.test(handle))throw publicError('Un produit du catalogue est invalide.');
      return json(new URL(`products/${encodeURIComponent(handle)}.js`,base),{credentials:'same-origin'});
    }));
    const cart=await json(new URL('cart.js',base),{credentials:'same-origin'});
    if(!/^[A-Z]{3}$/.test(cart.currency))throw publicError('La devise du panier est indisponible.');
    catalog=buildCatalog(settings,Object.fromEntries(handles.map((handle,index)=>[handle,values[index]])),cart.currency);
    onCatalog(publicCatalog(catalog));return catalog;
  }
  async function resolvePreviousAttempts(requestId) {
    for(const [oldId,previous] of states) {
      if(oldId===requestId || !previous.failed || previous.done || !previous.items)continue;
      let cart=await json(new URL('cart.js',base),{credentials:'same-origin'});
      if(missingCartItems(previous.items,cart,oldId).length===0){previous.done=true;previous.failed=false;continue;}
      const lines=cart.items.filter(line=>line.properties?._joliemap_request===oldId);
      if(!lines.length){previous.failed=false;continue;}
      // Roll back only the untouched lines created by this failed attempt. Never
      // delete a completed artwork or a line whose quantity the customer edited.
      if(lines.some(line=>typeof line.key!=='string' || line.quantity!==1))throw publicError('Une tentative précédente doit être vérifiée dans votre panier avant de créer une autre carte.',true);
      let updateError=null;
      try {await json(new URL('cart/update.js',base),{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates:Object.fromEntries(lines.map(line=>[line.key,0]))})});}
      catch(error){updateError=error;}
      cart=await json(new URL('cart.js',base),{credentials:'same-origin'});
      if(cart.items.some(line=>line.properties?._joliemap_request===oldId))throw publicError(updateError?.message || 'La tentative précédente doit être retirée du panier avant de réessayer.',true);
      previous.failed=false;
    }
  }
  async function processPurchase(data) {
    const conf=data.composition?.config;
    if(!enabled || !conf || data.composition.schema_version!==1 || !numericId(data.variant_id) ||
      !/^[0-9a-f-]{36}$/i.test(data.request_id) || JSON.stringify(data.composition).length>8192)throw publicError('Cette demande de carte est invalide.');
    if(settings.require_preview && (!data.preview || typeof data.preview.data_url!=='string' || !data.preview.data_url.startsWith('data:image/jpeg;base64,') || data.preview.data_url.length>220000))throw publicError('L’aperçu de votre carte doit être préparé avant son ajout. Réessayez.');
    const fingerprint=JSON.stringify([data.variant_id,data.composition]);
    let state=states.get(data.request_id);
    if(state && state.fingerprint!==fingerprint)throw publicError('La demande a changé. Rechargez l’éditeur.');
    if(state?.done)return {ok:true};
    const previous=catalog.variants.find(value=>key(value)===key(conf) && value.id===data.variant_id);
    const previousCurrency=catalog.currency;
    if(!previous)throw publicError('Cette combinaison n’est pas proposée.');
    const fresh=await refresh();
    const variant=fresh.variants.find(value=>key(value)===key(conf) && value.id===data.variant_id);
    if(!state) {
      if(!variant?.available)throw publicError('Cette combinaison vient d’être épuisée. Choisissez une autre option.');
      if(previous.price!==variant.price || previousCurrency!==fresh.currency)throw publicError('Le prix vient de changer. Vérifiez le nouveau tarif puis ajoutez votre carte.');
      state={fingerprint,saved:null,items:null,done:false,price:variant.price,currency:fresh.currency};states.set(data.request_id,state);
    } else if(!variant || state.price!==variant.price || state.currency!==fresh.currency) {
      if(variant){state.price=variant.price;state.currency=fresh.currency;}
      throw publicError('Le prix vient de changer. Vérifiez le nouveau tarif puis réessayez cet ajout.',true);
    }
    await resolvePreviousAttempts(data.request_id);
    if(!state.saved) {
      const saved=await json(endpoint,{method:'POST',credentials:'omit',headers:{'Content-Type':'application/json','Idempotency-Key':data.request_id},body:JSON.stringify({...data.composition,...(data.preview?{preview:data.preview}:{})})});
      if(data.preview && !saved.preview_url)throw publicError('L’aperçu de votre carte n’a pas été enregistré. Réessayez.',true);
      state.items=savedCartItems(data.composition,variant,saved,data.request_id,endpoint.origin);state.saved=saved;
    }
    let cart=await json(new URL('cart.js',base),{credentials:'same-origin'});
    let missing=missingCartItems(state.items,cart,data.request_id);
    if(missing.length) {
      // Retry only missing lines: a lost POST response or a partial stock error must not duplicate an artwork.
      let addError=null;
      try {await json(new URL('cart/add.js',base),{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:missing})});}
      catch(error){addError=error;}
      cart=await json(new URL('cart.js',base),{credentials:'same-origin'});
      missing=missingCartItems(state.items,cart,data.request_id);
      if(missing.length)throw publicError(addError?.message || 'Le panier doit encore être confirmé. Réessayez.',true);
    }
    state.done=true;state.failed=false;onAdded(cart);return {ok:true};
  }
  return {
    refresh,
    currentCatalog:()=>publicCatalog(catalog),
    purchase(data) {
      let fingerprint;
      try{fingerprint=JSON.stringify([data.variant_id,data.composition]);}catch{return Promise.resolve({ok:false,error:'La demande de carte est invalide.'});}
      if(fingerprints.has(data.request_id) && fingerprints.get(data.request_id)!==fingerprint)return Promise.resolve({ok:false,error:'La demande a changé. Rechargez l’éditeur.'});
      fingerprints.set(data.request_id,fingerprint);
      if(requests.has(data.request_id))return requests.get(data.request_id);
      const request=processPurchase(data).catch(error=>{const state=states.get(data.request_id);if(state && !state.done)state.failed=true;return {ok:false,error:error.message,retryable:error.retryable===true};});
      requests.set(data.request_id,request);
      request.then(result=>{if(!result.ok)requests.delete(data.request_id);});
      return request;
    },
  };
}

export function mountShopifyEditor(element,win=window) {
  if(element.dataset.mounted)return;
  element.dataset.mounted='true';
  const status=element.querySelector('[data-editor-status]'),iframe=element.querySelector('iframe');
  const settings=JSON.parse(element.querySelector('script[type="application/json"]').textContent);
  element.querySelector('[data-editor-cart]').hidden=true;
  let editor;
  try {editor=new URL(settings.editor_url);if(editor.protocol!=='https:' || editor.username || editor.password)throw new Error();}
  catch {status.textContent='L’éditeur sera disponible prochainement.';return;}
  editor.searchParams.set('embed','shopify');editor.searchParams.set('parent_origin',win.location.origin);
  const send=value=>iframe.contentWindow?.postMessage({channel:CHANNEL,protocol:PROTOCOL,...value},editor.origin);
  let parent;
  try {
    parent=createShopifyParent({settings,origin:win.location.origin,root:win.Shopify?.routes?.root || '/',
      onCatalog:catalog=>send({type:'catalog',catalog}),
      onAdded:cart=>{
        status.textContent='';
        element.querySelector('[data-editor-cart]').hidden=false;
        win.document.querySelectorAll('[data-cart-count]').forEach(node=>node.textContent=String(cart.item_count));
        win.document.querySelectorAll('[data-cart-count-bubble]').forEach(node=>node.classList.toggle('hide',!cart.item_count));
        win.document.dispatchEvent(new CustomEvent('joliemap:cart-added',{detail:{cart}}));
      }});
  }catch(error){status.textContent=error.message;return;}
  let loading=null;
  win.addEventListener('message',async event=>{
    const data=event.data;
    if(event.source!==iframe.contentWindow || event.origin!==editor.origin || !data || data.channel!==CHANNEL || data.protocol!==PROTOCOL)return;
    if(data.type==='ready') {
      try {
        loading ||= parent.refresh().finally(()=>loading=null);
        await loading;send({type:'init',catalog:parent.currentCatalog()});status.textContent='';
      }catch(error){status.textContent=error.message;send({type:'init',catalog:{currency:settings.currency || 'EUR',enabled:false,variants:[]}});}
    } else if(data.type==='purchase') {
      status.textContent='Enregistrement de votre carte…';
      const result=await parent.purchase(data);
      send({type:'purchase-result',request_id:data.request_id,...result});
      if(!result.ok)status.textContent=result.error;
    }
  });
  iframe.src=editor.toString();iframe.hidden=false;
  // Fill the viewport below Shopify navigation, leaving room for its preview bar.
  // Document coordinates keep the canvas stable when the customer scrolls.
  let heightFrame=0,previewBar=null;
  const measureHeight=()=>{
    heightFrame=0;
    const bar=win.document.getElementById('PBarNextFrameWrapper');
    if(bar && bar!==previewBar){previewBar=bar;resizeObserver?.observe(bar);}
    const bounds=bar?.getBoundingClientRect();
    const bottomInset=bounds?.height>0 && bounds.height<200 ? Math.max(0,win.innerHeight-bounds.top) : 0;
    const offset=Math.max(0,iframe.getBoundingClientRect().top+win.scrollY)+bottomInset;
    const value=`${offset}px`;
    if(element.style.getPropertyValue('--joliemap-editor-offset')!==value)element.style.setProperty('--joliemap-editor-offset',value);
  };
  const scheduleHeight=()=>{if(!heightFrame)heightFrame=win.requestAnimationFrame(measureHeight);};
  const resizeObserver=win.ResizeObserver?new win.ResizeObserver(scheduleHeight):null;
  for(const node of [win.document.querySelector('#shopify-section-header'),element.querySelector('.joliemap-editor__intro'),status]) {
    if(node)resizeObserver?.observe(node);
  }
  win.addEventListener('resize',scheduleHeight);
  win.addEventListener('load',scheduleHeight,{once:true});
  win.document.fonts?.ready.then(scheduleHeight);
  win.document.addEventListener('shopify:section:unload',event=>{
    if(!event.target.contains(element))return;
    resizeObserver?.disconnect();win.cancelAnimationFrame(heightFrame);
    win.removeEventListener('resize',scheduleHeight);win.removeEventListener('load',scheduleHeight);
  });
  scheduleHeight();
  iframe.addEventListener('error',()=>status.textContent='L’éditeur n’a pas pu être chargé. Rechargez cette page.');
}
if(typeof document!=='undefined') {
  document.querySelectorAll('[data-joliemap-editor]').forEach(element=>mountShopifyEditor(element));
  document.addEventListener('shopify:section:load',event=>event.target.querySelectorAll('[data-joliemap-editor]').forEach(element=>mountShopifyEditor(element)));
}
